import { execFile } from "node:child_process";
import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import { promisify } from "node:util";

const execFileAsync = promisify(execFile);

function systemdArgument(value: string): string {
  if (/[\r\n\0]/u.test(value)) throw new Error("Service executable paths cannot contain control characters");
  return `"${value.replaceAll("\\", "\\\\").replaceAll('"', '\\"').replaceAll("%", "%%").replaceAll("$", () => "$$")}"`;
}

function xml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&apos;");
}

export function renderSystemdUnit(node: string, entry: string): string {
  return `[Unit]\nDescription=Nori AI Gateway\nWants=network-online.target\nAfter=network-online.target\n\n[Service]\nType=simple\nExecStart=${systemdArgument(node)} ${systemdArgument(entry)} gateway run\nRestart=on-failure\nRestartSec=5\nTimeoutStopSec=20\nKillSignal=SIGTERM\nUMask=0077\nEnvironment=NODE_ENV=production\nNoNewPrivileges=true\n\n[Install]\nWantedBy=default.target\n`;
}

export function renderLaunchAgent(node: string, entry: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>\n<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">\n<plist version="1.0"><dict><key>Label</key><string>ai.nori.gateway</string><key>ProgramArguments</key><array><string>${xml(node)}</string><string>${xml(entry)}</string><string>gateway</string><string>run</string></array><key>EnvironmentVariables</key><dict><key>NODE_ENV</key><string>production</string></dict><key>RunAtLoad</key><true/><key>KeepAlive</key><dict><key>SuccessfulExit</key><false/></dict><key>ProcessType</key><string>Background</string><key>Umask</key><integer>63</integer></dict></plist>\n`;
}

export async function installService(): Promise<string> {
  const node = process.execPath;
  const entry = process.argv[1]!;
  if (process.platform === "linux") {
    const directory = path.join(os.homedir(), ".config/systemd/user");
    const filePath = path.join(directory, "nori-gateway.service");
    await fs.mkdir(directory, { recursive: true });
    await fs.writeFile(filePath, renderSystemdUnit(node, entry), { mode: 0o600 });
    await execFileAsync("systemctl", ["--user", "daemon-reload"]);
    await execFileAsync("systemctl", ["--user", "enable", "nori-gateway.service"]);
    return filePath;
  }
  if (process.platform === "darwin") {
    const directory = path.join(os.homedir(), "Library/LaunchAgents");
    const filePath = path.join(directory, "ai.nori.gateway.plist");
    await fs.mkdir(directory, { recursive: true });
    await fs.writeFile(filePath, renderLaunchAgent(node, entry), { mode: 0o600 });
    return filePath;
  }
  if (process.platform === "win32") {
    await execFileAsync("schtasks.exe", ["/Create", "/F", "/SC", "ONLOGON", "/TN", "Nori Gateway", "/TR", `\"${node}\" \"${entry}\" gateway run`]);
    return "Task Scheduler: Nori Gateway";
  }
  throw new Error(`Service installation is unsupported on ${process.platform}`);
}

export async function serviceAction(action: "start" | "stop" | "restart"): Promise<void> {
  if (process.platform === "linux") {
    await execFileAsync("systemctl", ["--user", action, "nori-gateway.service"]);
    return;
  }
  if (process.platform === "darwin") {
    const filePath = path.join(os.homedir(), "Library/LaunchAgents/ai.nori.gateway.plist");
    const domain = `gui/${process.getuid?.() ?? 0}`;
    if (action === "stop") await execFileAsync("launchctl", ["bootout", domain, filePath]);
    else {
      if (action === "restart") await execFileAsync("launchctl", ["bootout", domain, filePath]).catch(() => undefined);
      await execFileAsync("launchctl", ["bootstrap", domain, filePath]);
    }
    return;
  }
  if (process.platform === "win32") {
    if (action === "stop") await execFileAsync("schtasks.exe", ["/End", "/TN", "Nori Gateway"]);
    else {
      if (action === "restart") await execFileAsync("schtasks.exe", ["/End", "/TN", "Nori Gateway"]).catch(() => undefined);
      await execFileAsync("schtasks.exe", ["/Run", "/TN", "Nori Gateway"]);
    }
    return;
  }
  throw new Error(`Service control is unsupported on ${process.platform}`);
}

export async function uninstallService(): Promise<string> {
  if (process.platform === "linux") {
    const filePath = path.join(os.homedir(), ".config/systemd/user/nori-gateway.service");
    await execFileAsync("systemctl", ["--user", "disable", "--now", "nori-gateway.service"]).catch(() => undefined);
    await fs.rm(filePath, { force: true });
    await execFileAsync("systemctl", ["--user", "daemon-reload"]);
    return filePath;
  }
  if (process.platform === "darwin") {
    const filePath = path.join(os.homedir(), "Library/LaunchAgents/ai.nori.gateway.plist");
    const domain = `gui/${process.getuid?.() ?? 0}`;
    await execFileAsync("launchctl", ["bootout", domain, filePath]).catch(() => undefined);
    await fs.rm(filePath, { force: true });
    return filePath;
  }
  if (process.platform === "win32") {
    await execFileAsync("schtasks.exe", ["/Delete", "/F", "/TN", "Nori Gateway"]);
    return "Task Scheduler: Nori Gateway";
  }
  throw new Error(`Service uninstallation is unsupported on ${process.platform}`);
}
